import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Card, DataStatus} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {
  PriceMapRequestBody,
  SourcingVehicleDetailResponseBody,
  useSourcingGetPriceMapQuery,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useSourcingListAvailability} from '../../../../hooks/useSourcingListAvailability';
import {ChartCheckboxes} from './components/PriceMap/components/ChartCheckboxes';
import {SourcingVehicleCard} from './components/PriceMap/components/SourcingVehicleCard';
import {PriceMap} from './components/PriceMap/PriceMap';

const ITEMS_PER_PAGE = 1000;

interface PriceMapTabProps extends TestIdProps {
  vehicle: SourcingVehicleDetailResponseBody;
}

export function PriceMapTab({vehicle, ...props}: PriceMapTabProps) {
  const [checkboxes, setCheckboxes] = useState<{
    onStockChecked: boolean;
    soldRecentlyChecked: boolean;
    soldChecked: boolean;
  }>({
    onStockChecked: true,
    soldRecentlyChecked: true,
    soldChecked: true,
  });
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | undefined>();

  const recommendedPrice = vehicle.sourcingVehicle?.extraData?.predictedPrice;
  const listingPrice = vehicle.sourcingVehicle?.price?.withVat;

  const priceMapRequestBody: PriceMapRequestBody | undefined = isNotNil(vehicle.sourcingVehicle)
    ? {
        size: ITEMS_PER_PAGE,
        query_price: recommendedPrice ?? listingPrice ?? 0,
        fuelType: isNotNil(vehicle.sourcingVehicle.fuelType?.key)
          ? [vehicle.sourcingVehicle.fuelType?.key]
          : undefined,
        transmission: isNotNil(vehicle.sourcingVehicle.transmission?.key)
          ? [vehicle.sourcingVehicle.transmission?.key]
          : undefined,
        bodyStyle: isNotNil(vehicle.sourcingVehicle.carStyle?.key)
          ? [vehicle.sourcingVehicle.carStyle?.key]
          : undefined,
        drive: isNotNil(vehicle.sourcingVehicle.driveType?.key)
          ? [vehicle.sourcingVehicle.driveType?.key]
          : undefined,
        firstRegistrationOnYear: isNotNil(vehicle.sourcingVehicle.year)
          ? {
              gte: vehicle.sourcingVehicle.year,
              lte: vehicle.sourcingVehicle.year,
            }
          : undefined,
        power: isNotNil(vehicle.sourcingVehicle.power)
          ? {
              gte: vehicle.sourcingVehicle.power,
              lte: vehicle.sourcingVehicle.power,
            }
          : undefined,
        hideStockCars: !checkboxes.onStockChecked,
        hideSoldIn30: !checkboxes.soldRecentlyChecked,
        hideSoldIn90: !checkboxes.soldChecked,
      }
    : undefined;

  const {data, isLoading, isError} = useSourcingGetPriceMapQuery(
    isNotNil(priceMapRequestBody)
      ? {
          externalId: vehicle.sourcingVehicle.adId,
          priceMapRequestBody,
        }
      : skipToken
  );

  const cars = data?.cars.filter((car) => car.ad_id !== vehicle.sourcingVehicle.adId) ?? [];
  const priceMapAdIds = cars.map((car) => car.ad_id).filter(isNotNil);

  useSourcingListAvailability({
    adIds: priceMapAdIds,
    enabled:
      isFeatureEnabled(featureFlags.SOURCING_AVAILABILITY_CHECK) &&
      !isLoading &&
      priceMapAdIds.length > 0,
    userLoadKey: [
      vehicle.sourcingVehicle.adId,
      checkboxes.onStockChecked,
      checkboxes.soldRecentlyChecked,
      checkboxes.soldChecked,
      recommendedPrice ?? listingPrice ?? 0,
    ].join('|'),
  });

  return (
    <VStack spacing={4} width="100%">
      <DataStatus isLoading={isLoading} isError={isError} minHeight="100%">
        <Card
          title={i18n.t('general.labels.priceMap')}
          tooltip={i18n.t('entity.vehicle.labels.priceMapTooltip')}
        >
          <VStack spacing={4} height={120}>
            <ChartCheckboxes
              values={checkboxes}
              onChange={setCheckboxes}
              cars={cars}
              data-testid={suffixTestId('chartCheckboxes', props)}
            />
            <PriceMap
              cars={cars}
              queryPriceIndex={data?.query_original_price_index ?? 0}
              vehicle={vehicle}
              selectedVehicleId={selectedVehicleId ?? null}
              setSelectedVehicleId={(id) => {
                setSelectedVehicleId(id ?? undefined);
              }}
              data-testid={suffixTestId('priceMap', props)}
            />
          </VStack>
        </Card>

        <Show when={isNotNil(selectedVehicleId)}>
          <SourcingVehicleCard
            externalId={selectedVehicleId!}
            data-testid={suffixTestId('sourcingVehicleCard', props)}
          />
        </Show>
      </DataStatus>
    </VStack>
  );
}
